import { useCallback, useEffect, useSyncExternalStore } from 'react';
import { useAppSelector } from '@/shared/lib/redux/hooks';
import type { CartState } from '../model/types';
import { readCart } from './cartStorage';

/** Каждый `useClientCart` держит своё состояние (источник правды — localStorage), поэтому другой
 *  экран о правке корзины сам не узнает. Страницы корзины/каталога сообщают об изменении этим
 *  событием, а виды «только чтение» (бейдж на вкладке «Корзина») перечитывают хранилище. */
const CART_CHANGED_EVENT = 'client-cart:changed';

export function announceCartChange(): void {
  window.dispatchEvent(new Event(CART_CHANGED_EVENT));
}

/** Страница, владеющая `useClientCart`, вызывает это после него — и каждая правка корзины доходит до бейджа. */
export function useAnnounceCartChanges(cart: CartState): void {
  useEffect(() => {
    announceCartChange();
  }, [cart]);
}

function subscribe(onChange: () => void): () => void {
  window.addEventListener(CART_CHANGED_EVENT, onChange);
  // Другая вкладка того же браузера правит ту же корзину.
  window.addEventListener('storage', onChange);
  return () => {
    window.removeEventListener(CART_CHANGED_EVENT, onChange);
    window.removeEventListener('storage', onChange);
  };
}

/** Число позиций в корзине текущей учётной записи — для бейджа вкладки. Только чтение: сама
 *  корзина меняется исключительно через `useClientCart`. */
export function useCartItemCount(storeId: string): number {
  const userId = useAppSelector((s) => s.auth.user?.id);
  const getSnapshot = useCallback(() => (userId ? readCart(storeId, userId).items.length : 0), [storeId, userId]);
  return useSyncExternalStore(subscribe, getSnapshot, () => 0);
}
